import {
  SkyAccordion,
  SkyAccordionItem,
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDropdown,
  SkyInput,
  SkyRadioGroup,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import {
  KEEP_SOURCE_CASE_UNAVAILABLE,
  LOWER_CASE_UNAVAILABLE,
  METADATA_COLUMNS_HINT,
  METADATA_COLUMNS_LABEL,
  TABLE_NAME_CASE_CHANGE_NOTE,
  TABLE_NAME_CASE_DBT_NOTE,
  TABLE_NAME_CASE_MOVE_NOTE,
  TABLE_NAME_CASE_HINT,
  TABLE_NAME_CASE_LABELS,
  type InstanceBindingView,
  type SecretPointer,
} from "@shared/types/instances";
import { effectiveTableNameCase, type TableNameCase } from "@shared/warehouse/targetTableName";
import {
  DEFAULT_LOAD_SCHEMA,
  type BindingSecretCopy,
  type BindingSecretField,
} from "@shared/types/instancesAdmin";
import {
  missingSnowflakeLoadFields,
  snowflakeLoadFieldIssue,
  touchesSnowflakeLoad,
  type SnowflakeLoadField,
} from "@shared/warehouse/snowflakeLoadFields";
import { passphraseRefusal, pemKeyKind } from "@/lib/pemKey";
import { bindingSecretStored } from "@/lib/secretStored";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { SecretReferenceField } from "./SecretReferenceField";

/**
 * Where this instance lands data (canvas 08 blocks 01, 02 and 05): the
 * **Warehouse** tab of the destination page (Session 10 P5).
 *
 * One form, two destination types: the save bar and the re-authentication
 * behaviour are identical, and only the credential block changes with the type.
 * Splitting it into two forms would duplicate the parts that carry the rules.
 *
 * Session 10 moved two things out of this form. The dbt repository is the
 * Transformation source tab (`DbtSourceForm`), saved through its own route with
 * `dbt_jobs.write`. Storage is the platform's: the instance route refuses
 * it, and its names are shown to platform admins only, in the page's "Platform
 * resources" section (`PlatformResources`, owner 2026-09-28). The Snowflake stage
 * SAS this screen once wrote is retired (#399): a stage authenticates with a storage
 * integration only.
 *
 * Every credential is a `SecretReferenceField`. There is nothing in this
 * component that could render a secret value, because the view it is given never
 * contains one (SEC-2).
 *
 * The read-only variant is a real form with a visible "Platform admin only" note,
 * not a hidden one: `instance.binding.write` is PlatformAdmin-only, and an
 * instance admin who can see where their data goes but not change it is better
 * served by being told so than by a tab that vanished.
 *
 * Those fields are `readOnly` and never `disabled`, so they stay in the tab order
 * and a screen reader can still read the value out. `readOnly` on a native input
 * is what exposes `aria-readonly` in the accessibility tree; `SkyInput` does not
 * forward an explicit `aria-readonly`, and JSX does not type-check hyphenated
 * attributes, so passing one would have looked right and done nothing.
 */

/** The plain-text columns of the binding, by destination type. */
export type BindingTextField =
  | "snowflakeAccount"
  | "snowflakeWarehouse"
  | "snowflakeDatabase"
  | "snowflakeSchema"
  | "snowflakeRole"
  | "snowflakeUser"
  | "snowflakeStorageIntegration"
  | "snowflakeStageName"
  | "databricksHost"
  | "databricksSpClientId"
  | "databricksWarehouseId"
  | "dbtRepoUrl"
  | "dbtBranch"
  | "dbtProjectSubdir"
  | "adlsAccount"
  | "adlsContainerStaging"
  | "adlsContainerLake";

export const SNOWFLAKE_TEXT_FIELDS: BindingTextField[] = [
  "snowflakeAccount",
  "snowflakeWarehouse",
  "snowflakeDatabase",
  "snowflakeSchema",
  "snowflakeRole",
  "snowflakeUser",
];

/**
 * ADR 0011 (2026-10-08): the loader identity. The server leaves these out below TenantAdmin
 * (`identityWithheld`); the form then shows no field for them and says who can see them.
 */
const LOADER_IDENTITY_TEXT_FIELDS: ReadonlySet<BindingTextField> = new Set([
  "snowflakeUser",
  "snowflakeRole",
  "databricksSpClientId",
]);

/** The one line shown in place of the loader identity (ADR 0011). */
export const IDENTITY_WITHHELD_COPY = {
  snowflake:
    "skyData signs in to Snowflake with its own service account. Your tenant admin can see its name and role.",
  databricks:
    "skyData signs in to Databricks with its own service principal. Your tenant admin can see its client id.",
} as const;

export const DATABRICKS_TEXT_FIELDS: BindingTextField[] = [
  "databricksHost",
  "databricksSpClientId",
  "databricksWarehouseId",
];

/**
 * The text fields the Warehouse tab edits and saves. Storage is refused by the
 * instance route and the dbt fields have their own tab and route, so neither is
 * ever part of a Warehouse save body (Session 10 P5).
 */
export const WAREHOUSE_TEXT_FIELDS: BindingTextField[] = [
  ...SNOWFLAKE_TEXT_FIELDS,
  "snowflakeStorageIntegration",
  "snowflakeStageName",
  ...DATABRICKS_TEXT_FIELDS,
];

/*
 * The dbt fields and their refusals (`dbtFieldRefusal`, `dbtDraftRefusals`, session 6 N-9)
 * moved to the dbt repository tab in Session 10 (P5): `DbtSourceForm` applies the same
 * shared rules (`dbtRepoUrlRefusal`, `dbtRefRefusal`, `dbtProjectSubdirRefusal`) through
 * `dbtSourceRefusals`, and the storage fields are `PlatformResources` (platform admins only).
 */

export const FIELD_LABELS: Record<BindingTextField, string> = {
  snowflakeAccount: "Account",
  snowflakeWarehouse: "Warehouse",
  snowflakeDatabase: "Database",
  snowflakeSchema: "Schema",
  snowflakeRole: "Role",
  snowflakeUser: "User",
  snowflakeStorageIntegration: "Storage integration",
  snowflakeStageName: "Stage name",
  databricksHost: "Workspace host",
  databricksSpClientId: "Service principal client id",
  databricksWarehouseId: "SQL warehouse id",
  dbtRepoUrl: "dbt repository URL",
  dbtBranch: "dbt branch",
  dbtProjectSubdir: "dbt project subdirectory",
  adlsAccount: "ADLS account",
  adlsContainerStaging: "Staging container",
  adlsContainerLake: "Lake container",
};

/**
 * Round 4 item 12a: the Schema field says what lands there. The owner expected loads in a bronze
 * schema and found STAGING (dev-01's typed value). New instances now start with BRONZE
 * (`DEFAULT_LOAD_SCHEMA`, written by the create); an existing binding is never changed, so an
 * empty one offers BRONZE as a suggestion. #924: a suggestion is a "Use BRONZE" button that
 * types it, never a grey placeholder, which QA read as a value and saved empty.
 */
const FIELD_HINTS: Partial<Record<BindingTextField, string>> = {
  snowflakeSchema: `Each import's tables land here, as loaded from the source (the bronze layer). New instances start with ${DEFAULT_LOAD_SCHEMA}. dbt builds in this schema too unless its project names another.`,
};

/** #924: the Snowflake fields a load needs, marked required on the form. */
const SNOWFLAKE_REQUIRED_TEXT: ReadonlySet<BindingTextField> = new Set([
  "snowflakeAccount",
  "snowflakeUser",
  "snowflakeDatabase",
  "snowflakeSchema",
]);

/** A field's error from the last Save that was refused (#924 fields are refused by the server too, #1248). */
export type DestinationRefusals = Partial<Record<BindingTextField | BindingSecretField, string>>;

/** Whether the draft leaves a secret set: a staged value or copy, or the stored one kept. */
function secretSet(
  draft: DestinationDraft,
  binding: InstanceBindingView,
  field: "snowflakePrivateKey" | "snowflakeKeyPassphrase",
): boolean {
  const staged = draft.secrets[field];
  if (staged === null) return false;
  if (staged !== undefined) return true;
  return bindingSecretStored(binding, field);
}

/**
 * What Save refuses before anything is sent, by field.
 *
 * - #924: a save that touches what a Snowflake load reads (`touchesSnowflakeLoad`, the server's
 *   own test) must leave the account, user, private key, database and schema set
 *   (`missingSnowflakeLoadFields`, the rule the binding save and the runner's gate apply).
 * - #925: a newly pasted key's PEM header says whether it is encrypted; the passphrase must then
 *   be set, or (for an unencrypted key) cleared. Read here only, never sent or logged.
 */
export function destinationRefusals(
  draft: DestinationDraft,
  binding: InstanceBindingView,
  body: Parameters<typeof touchesSnowflakeLoad>[0] | null,
): DestinationRefusals {
  if (draft.destinationType !== "snowflake") return {};
  const refusals: DestinationRefusals = {};
  if (body && touchesSnowflakeLoad(body)) {
    const missing: SnowflakeLoadField[] = missingSnowflakeLoadFields({
      snowflakeAccount: draft.text.snowflakeAccount,
      snowflakeUser: draft.text.snowflakeUser,
      snowflakeDatabase: draft.text.snowflakeDatabase,
      snowflakeSchema: draft.text.snowflakeSchema,
      snowflakePrivateKey: secretSet(draft, binding, "snowflakePrivateKey"),
    });
    for (const field of missing) refusals[field] = snowflakeLoadFieldIssue(field);
  }
  const key = draft.secrets.snowflakePrivateKey;
  if (typeof key === "string") {
    const refusal = passphraseRefusal(
      pemKeyKind(key),
      secretSet(draft, binding, "snowflakeKeyPassphrase"),
    );
    if (refusal) refusals.snowflakeKeyPassphrase = refusal;
  }
  return refusals;
}

/** #925: the passphrase is asked for only when the private key is encrypted. */
export const KEY_PASSPHRASE_LABEL = "Key passphrase (optional)";
export const KEY_PASSPHRASE_HINT =
  "Only for an encrypted private key, whose first line reads ENCRYPTED PRIVATE KEY. Leave empty for an unencrypted key.";

export interface DestinationDraft {
  destinationType: "snowflake" | "databricks";
  text: Record<BindingTextField, string>;
  /**
   * A value held for the save, null to clear the reference, or (B28, platform admins only) a
   * copy of an existing vault secret named by its source. Absent = untouched.
   */
  secrets: Partial<Record<BindingSecretField, string | null | BindingSecretCopy>>;
  /** Round 6 (owner 2026-10-01 23:10): how a loaded table's name is spelled. */
  tableNameCase: TableNameCase;
  /** #2466: add `_SKYDATA_SYNCED_AT` and `_SKYDATA_RUN_ID` to every Snowflake table. Off by default. */
  metadataColumns: boolean;
}

/**
 * #2466: the draft's skyData columns setting differs from the saved one (absent reads off). Only
 * on a Snowflake draft: the checkbox is hidden on Databricks, so a value ticked before the type
 * moved is neither sent nor summarised.
 */
export function metadataColumnsChanged(
  draft: DestinationDraft,
  binding: InstanceBindingView,
): boolean {
  return (
    draft.destinationType === "snowflake" &&
    draft.metadataColumns !== (binding.metadataColumns === true)
  );
}

export function draftFromBinding(binding: InstanceBindingView): DestinationDraft {
  const text = {} as Record<BindingTextField, string>;
  for (const key of Object.keys(FIELD_LABELS) as BindingTextField[]) {
    text[key] = binding[key] ?? "";
  }
  return {
    destinationType: binding.destinationType,
    text,
    secrets: {},
    tableNameCase: binding.tableNameCase ?? "native",
    metadataColumns: binding.metadataColumns === true,
  };
}

interface FieldProps {
  draft: DestinationDraft;
  readOnly: boolean;
  onTextChange: (field: BindingTextField, value: string) => void;
}

function TextField({
  field,
  draft,
  readOnly,
  onTextChange,
  hint,
  required,
  suggestion,
  refusal,
}: FieldProps & {
  field: BindingTextField;
  hint?: string;
  required?: boolean;
  /** #924: a value the field offers to type for the admin while it is empty; never a placeholder. */
  suggestion?: string;
  refusal?: string | null;
}) {
  const offer = suggestion && !readOnly && draft.text[field] === "" ? suggestion : null;
  return (
    <div className="space-y-1">
      <SkyInput
        label={FIELD_LABELS[field]}
        value={draft.text[field]}
        readOnly={readOnly}
        required={required}
        hint={hint ?? FIELD_HINTS[field]}
        validationState={refusal ? "error" : "none"}
        validationMessage={refusal ?? undefined}
        testId={`binding-${field}`}
        onChange={(event) => onTextChange(field, event.target.value)}
      />
      {offer ? (
        <SkyButton
          appearance="subtle"
          size="small"
          testId={`binding-${field}-use-suggested`}
          onClick={() => onTextChange(field, offer)}
        >
          {`Use ${offer}`}
        </SkyButton>
      ) : null}
    </div>
  );
}

export function DestinationForm({
  binding,
  draft,
  readOnly,
  onTextChange,
  onTypeChange,
  onCaseChange,
  onMetadataColumnsChange,
  onReplaceSecret,
  onClearSecret,
  onDiscardSecret,
  secretErrors,
  refusals,
  suggestedDatabase,
}: FieldProps & {
  binding: InstanceBindingView;
  /**
   * Round 5: the Snowflake database this instance's name suggests (`<TENANT>_<ENVIRONMENT>`,
   * `suggestedWarehouseDatabase`). #924: offered as a "Use <name>" button beside the empty field,
   * which types it; never a placeholder, and never written on open (that would make the page
   * unsaved on open and rewrite the binding on the next save).
   */
  suggestedDatabase?: string;
  onTypeChange: (type: "snowflake" | "databricks") => void;
  /** Absent, the field is shown read only (a host that does not edit it). */
  onCaseChange?: (tableNameCase: TableNameCase) => void;
  /** #2466. Absent, the checkbox is shown read only (a host that does not edit it). */
  onMetadataColumnsChange?: (metadataColumns: boolean) => void;
  onReplaceSecret: (field: BindingSecretField) => void;
  onClearSecret: (field: BindingSecretField) => void;
  onDiscardSecret: (field: BindingSecretField) => void;
  /** The server's sentence per secret field from the last refused save (B28). */
  secretErrors?: Partial<Record<BindingSecretField, string>>;
  /** The fields Save refused before sending (#924, #925): `destinationRefusals`. */
  refusals?: DestinationRefusals;
}) {
  const withheld = binding.identityWithheld === true;
  const fixed = new Set(binding.fixedByDeployment ?? []);
  const shownFields = (fields: BindingTextField[]): BindingTextField[] =>
    withheld ? fields.filter((field) => !LOADER_IDENTITY_TEXT_FIELDS.has(field)) : fields;
  const secretProps = (field: BindingSecretField, pointer: SecretPointer) => ({
    pointer,
    // SEC-17: a reader without instance.settings.write gets no pointer, only whether one is stored.
    stored: binding.secretsStored?.[field],
    staged: draft.secrets[field],
    error: secretErrors?.[field] ?? refusals?.[field],
    readOnly,
    testId: `secret-${field}`,
    onReplace: readOnly ? undefined : () => onReplaceSecret(field),
    onClear: readOnly ? undefined : () => onClearSecret(field),
    onDiscardStaged: readOnly ? undefined : () => onDiscardSecret(field),
  });

  return (
    <div className="space-y-4">
      <div className="max-w-md">
        <SkyDropdown
          label="Destination type"
          testId="destination-type"
          disabled={readOnly}
          selectedOptions={[draft.destinationType]}
          options={[
            { value: "snowflake", text: "Snowflake" },
            // Owner 2026-10-13 (2B): shown, never offered as a new choice. A binding that is
            // already Databricks keeps it selected and reads "Coming soon".
            { value: "databricks", text: "Databricks (coming soon)", disabled: true },
          ]}
          onOptionSelect={(values) =>
            onTypeChange((values[0] as "snowflake" | "databricks") ?? draft.destinationType)
          }
        />
        <SkyText as="p" size={200} color="muted">
          Secrets are stored securely before the settings are saved. Changing the type leaves the
          other type&apos;s fields alone until you save.
        </SkyText>
      </div>

      {/* #2501: a field the deployment policy fixes is not rendered at all, not even read only. */}
      {fixed.has("tableNameCase") ? null : (
        <TableNameCaseField
          draft={draft}
          saved={binding.tableNameCase ?? "native"}
          movesDestination={movesDestination(draft, binding)}
          dbtConnected={Boolean(binding.dbtRepoUrl?.trim())}
          readOnly={readOnly || !onCaseChange}
          onChange={(value) => onCaseChange?.(value)}
        />
      )}

      {draft.destinationType === "snowflake" && !fixed.has("metadataColumns") ? (
        <div className="max-w-md" data-testid="metadata-columns">
          <SingleCheckbox
            label={METADATA_COLUMNS_LABEL}
            description={METADATA_COLUMNS_HINT}
            testId="binding-metadata-columns"
            checked={draft.metadataColumns}
            disabled={readOnly || !onMetadataColumnsChange}
            onChange={(checked) => onMetadataColumnsChange?.(checked)}
          />
        </div>
      ) : null}

      {draft.destinationType === "snowflake" ? (
        <SkyCard
          appearance="outline"
          testId="snowflake-card"
          header={{
            title: "Snowflake",
            action: readOnly ? (
              <SkyBadge appearance="tint" color="subtle">
                Read-only
              </SkyBadge>
            ) : undefined,
          }}
        >
          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shownFields(SNOWFLAKE_TEXT_FIELDS).map((field) => (
              <TextField
                key={field}
                field={field}
                draft={draft}
                readOnly={readOnly}
                onTextChange={onTextChange}
                required={!readOnly && SNOWFLAKE_REQUIRED_TEXT.has(field)}
                refusal={refusals?.[field] ?? null}
                {...(field === "snowflakeSchema" ? { suggestion: DEFAULT_LOAD_SCHEMA } : {})}
                {...(field === "snowflakeDatabase" && suggestedDatabase
                  ? {
                      suggestion: suggestedDatabase,
                      hint: `Suggested: ${suggestedDatabase}, the tenant's Name and this instance's environment.`,
                    }
                  : {})}
              />
            ))}
          </div>
          {withheld ? (
            <div className="mt-4">
              <SkyText as="p" size={200} color="muted" testId="binding-identity-withheld">
                {IDENTITY_WITHHELD_COPY.snowflake}
              </SkyText>
            </div>
          ) : null}
          <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
            <SecretReferenceField
              label="Private key"
              hint="Key-pair authentication. skyData stores the key securely and never shows it again."
              {...secretProps("snowflakePrivateKey", binding.snowflakePrivateKey)}
            />
            <SecretReferenceField
              label={KEY_PASSPHRASE_LABEL}
              hint={KEY_PASSPHRASE_HINT}
              {...secretProps("snowflakeKeyPassphrase", binding.snowflakeKeyPassphrase)}
            />
          </div>
          <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-2">
            <TextField
              field="snowflakeStorageIntegration"
              draft={draft}
              readOnly={readOnly}
              onTextChange={onTextChange}
              hint="Lets Snowflake read the staging container with its own identity."
            />
            <TextField
              field="snowflakeStageName"
              draft={draft}
              readOnly={readOnly}
              onTextChange={onTextChange}
              hint="The Snowflake stage the load copies from. Empty uses one skyData creates for this instance."
            />
          </div>
          <StageSettingsHelp />
        </SkyCard>
      ) : (
        <SkyCard
          appearance="outline"
          testId="databricks-card"
          header={{
            title: "Databricks",
            action: readOnly ? (
              <SkyBadge appearance="tint" color="subtle">
                Read-only
              </SkyBadge>
            ) : undefined,
          }}
        >
          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-3">
            {shownFields(DATABRICKS_TEXT_FIELDS).map((field) => (
              <TextField
                key={field}
                field={field}
                draft={draft}
                readOnly={readOnly}
                onTextChange={onTextChange}
              />
            ))}
          </div>
          {withheld ? (
            <div className="mt-4">
              <SkyText as="p" size={200} color="muted" testId="binding-identity-withheld">
                {IDENTITY_WITHHELD_COPY.databricks}
              </SkyText>
            </div>
          ) : null}
          <div className="mt-4">
            <SecretReferenceField
              label="Service principal secret"
              hint="Replace stores the new secret securely and switches to it in one step."
              {...secretProps("databricksSpSecret", binding.databricksSpSecret)}
            />
          </div>
        </SkyCard>
      )}
    </div>
  );
}

/**
 * #2459: whether the draft changes where tables land (type, account, database, schema), read as
 * the server reads a move (`destinationMoves` in `BindingService`: trimmed, any letter case).
 */
function movesDestination(draft: DestinationDraft, binding: InstanceBindingView): boolean {
  const read = (value: string | null | undefined) => (value ?? "").trim().toLowerCase();
  if (draft.destinationType !== binding.destinationType) return true;
  return (["snowflakeAccount", "snowflakeDatabase", "snowflakeSchema"] as const).some(
    (field) => read(draft.text[field]) !== read(binding[field]),
  );
}

/**
 * Round 6 (owner 2026-10-01 23:10): the destination's "Table name case". Native is the default
 * and what every binding had. Keep source case and Lower case (#2459) are Snowflake only: on
 * Databricks each is shown disabled with the reason, and the field shows Native, which is what
 * that loader applies (`effectiveTableNameCase`). A change renames no table that already exists:
 * the hint says so, and while the choice differs from the saved one a note says it plainly.
 */
function TableNameCaseField({
  draft,
  saved,
  movesDestination,
  dbtConnected,
  readOnly,
  onChange,
}: {
  draft: DestinationDraft;
  saved: TableNameCase;
  /** #2459: the draft also changes the destination's type, account, database or schema. */
  movesDestination: boolean;
  /** #2488: a dbt repository is connected, so the dbt quoting note applies. */
  dbtConnected: boolean;
  readOnly: boolean;
  onChange: (value: TableNameCase) => void;
}) {
  const databricks = draft.destinationType === "databricks";
  const shown = effectiveTableNameCase(draft.destinationType, draft.tableNameCase);
  const changed = !readOnly && shown !== effectiveTableNameCase(draft.destinationType, saved);
  const snowflakeOnly = (value: "source" | "lower") => ({
    value,
    label: databricks
      ? `${TABLE_NAME_CASE_LABELS[value]} (Snowflake only)`
      : TABLE_NAME_CASE_LABELS[value],
    disabled: databricks,
  });
  return (
    <div className="max-w-md" data-testid="table-name-case">
      {/* Walk 2026-10-02, D17: a few choices are radios; a SkyDropdown always opens with a
          search box, which a short list does not need. */}
      <SkyRadioGroup
        label="Table name case"
        ariaLabel="Table name case"
        testId="binding-table-name-case"
        layout="horizontal"
        disabled={readOnly}
        value={shown}
        options={[
          { value: "native", label: TABLE_NAME_CASE_LABELS.native },
          snowflakeOnly("source"),
          snowflakeOnly("lower"),
        ]}
        onChange={(value) => {
          if (value === "native" || value === "source" || value === "lower") onChange(value);
        }}
      />
      <SkyText as="p" size={200} color="muted" block>
        {TABLE_NAME_CASE_HINT}
      </SkyText>
      {dbtConnected && (shown === "source" || shown === "lower") ? (
        <SkyText as="p" size={200} block testId="table-name-case-dbt-note">
          {TABLE_NAME_CASE_DBT_NOTE}
        </SkyText>
      ) : null}
      {changed ? (
        <SkyText as="p" size={200} block testId="table-name-case-change-note">
          {movesDestination ? TABLE_NAME_CASE_MOVE_NOTE : TABLE_NAME_CASE_CHANGE_NOTE}
        </SkyText>
      ) : null}
      {databricks ? (
        <SkyText as="p" size={200} color="muted" block testId="table-name-case-unavailable">
          {KEEP_SOURCE_CASE_UNAVAILABLE} {LOWER_CASE_UNAVAILABLE}
        </SkyText>
      ) : null}
    </div>
  );
}

/**
 * When to set the storage integration and the stage name (owner, 2026-09-28: "What is the use
 * of Storage integration and Stage name?"). Collapsed: the one-line hints say what each field
 * does; this says when to set it, for the reader who needs to decide. The facts behind it are
 * the load step's (`runner/snowflake/sqlGen.ts`: `buildEnsureStage`, `stageAlterDecision`,
 * `defaultStageName`).
 */
function StageSettingsHelp() {
  return (
    <SkyAccordion
      testId="stage-settings-help"
      defaultOpenItems={[]}
      chevronPosition="start"
      className="mt-2"
    >
      <SkyAccordionItem
        value="stage-settings-help"
        heading="Learn more about the storage integration and the stage name"
      >
        <div className="space-y-2" data-testid="stage-settings-help-body">
          <SkyText as="p" size={200} block>
            Snowflake reads the staging container with the storage integration&apos;s own identity,
            so no credential for the container is stored or rotated. Your Snowflake admin creates it
            for this storage account. Enter its name here.
          </SkyText>
          <SkyText as="p" size={200} block>
            Set a stage name only when your team manages its own stage in Snowflake, for example one
            created against a storage integration with usage granted to skyData. The stage must
            point at this instance&apos;s staging container: the load creates it if it is missing
            and, with a storage integration, changes it only when its location or integration
            differs. Leave it empty and skyData creates one stage per instance and keeps it up to
            date.
          </SkyText>
        </div>
      </SkyAccordionItem>
    </SkyAccordion>
  );
}
